<template>
  <div class="carousel-container">
    <template v-if="hasSlides">
      <div class="carousel-layout">
        <router-link
          v-if="mobileTopFeature"
          :to="`/noticia/${mobileTopFeature.id}`"
          class="mobile-feature-card mobile-feature-card--top"
          :style="getSideCardStyle(mobileTopFeature)"
        >
          <div class="side-card-overlay"></div>
          <div class="side-card-info">
            <span class="side-card-category">{{ mobileTopFeature.category }}</span>
            <h3 class="side-card-title">
              <span class="title-mobile">{{ truncateTitle(mobileTopFeature.title) }}</span>
              <span class="title-desktop">{{ mobileTopFeature.title }}</span>
            </h3>
            <span class="side-card-date">{{ mobileTopFeature.date }}</span>
          </div>
        </router-link>

        <div class="side-column side-left" v-if="sideLeftSlides.length">
          <router-link
            v-for="(slide, index) in sideLeftSlides"
            :key="`left-${slide?.id || index}`"
            :to="`/noticia/${slide.id}`"
            class="side-card"
            :style="getSideCardStyle(slide)"
          >
            <div class="side-card-overlay"></div>
            <div class="side-card-info">
              <span class="side-card-category">{{ slide.category }}</span>
              <h3 class="side-card-title">
                <span class="title-mobile">{{ truncateTitle(slide.title) }}</span>
                <span class="title-desktop">{{ slide.title }}</span>
              </h3>
              <span class="side-card-date">{{ slide.date }}</span>
            </div>
          </router-link>
        </div>

        <div class="carousel-main">
          <div class="carousel carousel-reveal">
            <button class="carousel-btn prev" @click="prevSlide" aria-label="Anterior">
              &#10094;
            </button>
            
            <div class="carousel-content">
              <router-link :to="`/noticia/${currentSlide.id}`" :key="currentIndex" class="carousel-slide">
                <div class="slide-image" :style="{ backgroundImage: `url(${currentSlide.image})` }">
                  <div class="slide-overlay"></div>
                </div>
                <div class="slide-info">
                  <h2 class="slide-title">
                    <span class="title-mobile">{{ truncateTitle(currentSlide.title) }}</span>
                    <span class="title-desktop">{{ currentSlide.title }}</span>
                  </h2>
                  <div class="slide-meta">
                    <span class="slide-date">{{ currentSlide.date }}</span>
                    <span class="slide-category">{{ currentSlide.category }}</span>
                  </div>
                </div>
              </router-link>
            </div>
            
            <button class="carousel-btn next" @click="nextSlide" aria-label="Próximo">
              &#10095;
            </button>
          </div>

          <div class="carousel-indicators">
            <span 
              v-for="(slide, index) in slides" 
              :key="index"
              :class="['indicator', { active: index === currentIndex }]"
              @click="goToSlide(index)"
            ></span>
          </div>
        </div>

        <div class="side-column side-right" v-if="sideRightSlides.length">
          <router-link
            v-for="(slide, index) in sideRightSlides"
            :key="`right-${slide?.id || index}`"
            :to="`/noticia/${slide.id}`"
            class="side-card"
            :style="getSideCardStyle(slide)"
          >
            <div class="side-card-overlay"></div>
            <div class="side-card-info">
              <span class="side-card-category">{{ slide.category }}</span>
              <h3 class="side-card-title">
                <span class="title-mobile">{{ truncateTitle(slide.title) }}</span>
                <span class="title-desktop">{{ slide.title }}</span>
              </h3>
              <span class="side-card-date">{{ slide.date }}</span>
            </div>
          </router-link>
        </div>

        <router-link
          v-if="mobileBottomFeature"
          :to="`/noticia/${mobileBottomFeature.id}`"
          class="mobile-feature-card mobile-feature-card--bottom"
          :style="getSideCardStyle(mobileBottomFeature)"
        >
          <div class="side-card-overlay"></div>
          <div class="side-card-info">
            <span class="side-card-category">{{ mobileBottomFeature.category }}</span>
            <h3 class="side-card-title">{{ mobileBottomFeature.title }}</h3>
            <span class="side-card-date">{{ mobileBottomFeature.date }}</span>
          </div>
        </router-link>
      </div>
    </template>
    <div v-else class="carousel-empty" aria-live="polite">
      <p>Nenhuma notícia em destaque no momento.</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NewsCarousel',
  props: {
    slides: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      currentIndex: 0,
      autoplayInterval: null
    }
  },
  computed: {
    hasSlides() {
      return Array.isArray(this.slides) && this.slides.length > 0
    },
    currentSlide() {
      if (!this.hasSlides) {
        return {}
      }
      const safeIndex = Math.min(this.currentIndex, this.slides.length - 1)
      return this.slides[safeIndex] || {}
    },
    sideCardPool() {
      if (!this.hasSlides) {
        return []
      }
      const base = this.slides.slice(0, 4)
      if (base.length >= 4 || this.slides.length === 0) {
        return base
      }

      const extended = [...base]
      let index = 0
      while (extended.length < 4 && index < this.slides.length * 2) {
        const candidate = this.slides[index % this.slides.length]
        extended.push(candidate)
        index += 1
      }

      return extended.slice(0, 4)
    },
    sideLeftSlides() {
      return this.sideCardPool.slice(0, 2)
    },
    sideRightSlides() {
      return this.sideCardPool.slice(2, 4)
    },
    mobileFeaturePool() {
      if (!this.hasSlides) {
        return []
      }
      const base = this.slides.slice(0, 2)
      if (base.length >= 2 || this.slides.length === 0) {
        return base
      }

      const extended = [...base]
      let index = 0
      while (extended.length < 2 && index < this.slides.length * 2) {
        const candidate = this.slides[index % this.slides.length]
        extended.push(candidate)
        index += 1
      }

      return extended.slice(0, 2)
    },
    mobileTopFeature() {
      return this.mobileFeaturePool[0] || null
    },
    mobileBottomFeature() {
      return this.mobileFeaturePool[1] || null
    }
  },
  watch: {
    slides: {
      immediate: true,
      handler(newSlides) {
        if (!Array.isArray(newSlides) || newSlides.length === 0) {
          this.currentIndex = 0
          this.stopAutoplay()
          return
        }
        this.currentIndex = 0
        if (!this.autoplayInterval) {
          this.startAutoplay()
        }
      }
    }
  },
  mounted() {
    if (this.hasSlides) {
      this.startAutoplay()
    }
  },
  beforeUnmount() {
    this.stopAutoplay();
  },
  methods: {
    truncateTitle(title) {
      if (!title) return ''
      const words = title.split(' ')
      if (words.length > 10) {
        return words.slice(0, 10).join(' ') + '...'
      }
      return title
    },
    nextSlide() {
      if (!this.hasSlides) return
      this.currentIndex = (this.currentIndex + 1) % this.slides.length;
      this.resetAutoplay();
    },
    prevSlide() {
      if (!this.hasSlides) return
      this.currentIndex = this.currentIndex === 0 ? this.slides.length - 1 : this.currentIndex - 1;
      this.resetAutoplay();
    },
    goToSlide(index) {
      if (!this.hasSlides) return
      this.currentIndex = index;
      this.resetAutoplay();
    },
    getSideCardStyle(slide) {
      if (!slide || !slide.image) {
        return {
          backgroundColor: 'rgba(23, 29, 58, 0.55)'
        }
      }
      return {
        backgroundImage: `url(${slide.image})`
      }
    },
    startAutoplay() {
      this.stopAutoplay()
      this.autoplayInterval = setInterval(() => {
        this.nextSlide();
      }, 10000);
    },
    stopAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
        this.autoplayInterval = null
      }
    },
    resetAutoplay() {
      this.stopAutoplay();
      if (this.hasSlides) {
        this.startAutoplay();
      }
    }
  }
}
</script>

<style scoped>
/* Title variants for mobile/desktop */
.title-mobile {
  display: none;
}

.title-desktop {
  display: inline;
}

@media (max-width: 768px) {
  .title-mobile {
    display: inline;
  }
  
  .title-desktop {
    display: none;
  }
}

.carousel-container {
  width: 85%;
  max-width: none;
  margin: 1rem auto 2.5rem;
  padding: 0;
  position: relative;
}

.carousel-layout {
  display: grid;
  grid-template-columns: minmax(200px, 0.5fr) minmax(0, 1.5fr) minmax(200px, 0.5fr);
  gap: 0.85rem;
  align-items: stretch;
  margin: 0 20px;
}

.carousel-main {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.side-column {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.carousel {
  position: relative;
  height: 420px;
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 35px 90px rgba(17, 24, 39, 0.12);
  border: none;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(8px);
  z-index: 1;
  overflow: hidden;
  opacity: 0;
  transform: translateY(24px);
  animation: fadeInUp 0.85s ease forwards;
}

.carousel-content {
  height: 100%;
  position: relative;
  overflow: hidden;
}

.carousel-slide {
  position: relative;
  height: 100%;
  display: flex;
  align-items: flex-end;
  text-decoration: none;
  color: inherit;
}

.slide-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
}

.slide-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, rgba(4, 11, 27, 0) 0%, rgba(4, 11, 27, 0.5) 55%, rgba(4, 11, 27, 0.95) 100%);
  box-shadow: 0 -40px 60px rgba(0, 0, 0, 0.4) inset;
}

.slide-info {
  position: relative;
  z-index: 1;
  padding: 1rem 2rem 1.6rem;
  color: white;
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
}

.slide-title {
  font-size: 20px;
  margin: 0 0 0.7rem 0;
  font-weight: 800;
  text-shadow: 0 2px 20px rgba(0,0,0,0.6);
  letter-spacing: -0.5px;
  line-height: 1.3;
}

.slide-meta {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.slide-date {
  font-size: 13px;
  opacity: 0.8;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.65);
}

.slide-category {
  display: inline-block;
  background: rgba(255, 255, 255, 0.92);
  color: #171d3a;
  padding: 0.35rem 1rem;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  box-shadow: none;
}

.carousel-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: #f7fbff;
  font-size: 48px;
  padding: 1rem;
  cursor: pointer;
  z-index: 2;
  transition: all 0.3s ease;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
}

.carousel-btn.prev {
  left: 1rem;
}

.carousel-btn.next {
  right: 1rem;
}

.carousel-indicators {
  display: flex;
  justify-content: center;
  gap: 0.8rem;
  margin-top: 0.5rem;
}

.indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(148, 171, 249, 0.4);
  cursor: pointer;
  transition: all 0.3s ease;
}

.indicator:hover {
  background: #94abf9;
}

.indicator.active {
  background: #94abf9;
  width: 40px;
  border-radius: 20px;
}

.carousel-empty {
  width: 100%;
  padding: 4rem 1rem;
  text-align: center;
  color: #64748b;
  font-weight: 500;
}

.side-card {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: 200px;
  border-radius: 0;
  overflow: hidden;
  text-decoration: none;
  color: #ffffff;
  background-size: cover;
  background-position: center;
  box-shadow: 0 25px 45px rgba(15, 23, 42, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  opacity: 0;
  transform: translateY(18px);
  animation: fadeInUp 0.9s ease forwards;
}

.side-column .side-card:nth-child(1) {
  animation-delay: 0.15s;
}

.side-column .side-card:nth-child(2) {
  animation-delay: 0.3s;
}

.side-card-overlay {
  position: absolute;
  inset: 0;
  opacity: 1;
  background:
    linear-gradient(180deg, rgba(4, 11, 27, 0.15) 0%, rgba(4, 11, 27, 0.45) 45%, rgba(4, 11, 27, 0.85) 100%),
    linear-gradient(0deg, rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.25));
}

.side-card-info {
  position: relative;
  z-index: 1;
  padding: 0.6rem 0.8rem;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.side-card-category {
  display: inline-block;
  align-self: flex-start;
  padding: 0.2rem 0.75rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.2);
  font-size: 10px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.side-card-title {
  font-size: 14px;
  margin: 0;
  line-height: 1.3;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.75);
}

.side-card-date {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.85);
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.7);
}

.side-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 30px 55px rgba(15, 23, 42, 0.45);
}

.mobile-feature-card {
  display: none;
}

@media (max-width: 1100px) {
  .carousel-layout {
    grid-template-columns: 1fr;
  }

  .side-column {
    flex-direction: row;
    flex-wrap: wrap;
  }

  .side-card {
    flex: 1 1 calc(50% - 0.5rem);
    min-width: 220px;
  }
}

@media (max-width: 768px) {
  .carousel-container {
    width: 100%;
    margin: 0 auto 2rem;
    padding: 0;
  }

  .carousel-layout {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .side-column {
    display: none;
  }

  .carousel {
    height: 320px;
    border-radius: 0;
  }
    
  .slide-description {
    font-size: 16px;
  }
  
  .carousel-btn {
    padding: 0.35rem 0.9rem;
    font-size: 22px;
  }

  .mobile-feature-card {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    height: 180px;
    border-radius: 0;
    overflow: hidden;
    text-decoration: none;
    color: #ffffff;
    background-size: cover;
    background-position: center;
    box-shadow: 0 20px 40px rgba(15, 23, 42, 0.35);
    border: 1px solid rgba(255, 255, 255, 0.12);
    animation: fadeInUp 0.75s ease forwards;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(22px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
